import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CallingService } from '../calling.service';
import { OzonetelSDKCoreService } from './ozonetel-sdk-core.service';
import { OzonetelController } from './ozonetelController';

/**
 * Canonical transfer target shape used by the dialer UI.
 * - AgentId: display identity shown in autocomplete.
 * - transferTo: exact identifier that must be passed to SDK transfer API.
 * - fromSdk: marks targets discovered directly from SDK lists (higher priority).
 */
export interface TransferTarget {
  AgentId: string;
  transferTo: string;
  Extension?: string;
  label?: string;
  status?: string;
  fromSdk?: boolean;
}

/** Minimal result envelope consumed by widget snackbar/flow control. */
export interface WarmTransferResult {
  success: boolean;
  message: string;
  /** When true, calling widget hangs up Agent A after releaseDelayMs. */
  releaseAgentA?: boolean;
  blindTransfer?: boolean;
  /** Ms before Agent A hangup — longer for warm/REST so Agent B can ring. */
  releaseDelayMs?: number;
}

@Injectable({ providedIn: 'root' })
export class OzonetelTransferService {
  /**
   * SDK transfer list cache used during the same call session.
   * Reused by warm transfer API to avoid a second list request.
   */
  private cachedTransferList: any = null;

  constructor(
    private ozonetelSDKCore: OzonetelSDKCoreService,
    private callingService: CallingService
  ) {}

  /**
   * Builds transfer target options using live Ozonetel sources.
   *
   * Source order:
   * 1) DigitalConsole getTransferList (agent transfer list)
   * 2) DigitalConsole getConferenceList (extra live/eligible agents)
   * 3) Server fallback: getAgentLogins (only when SDK lists are empty)
   *
   * The result is deduped and excludes the current logged-in agent.
   */
  async loadTransferTargets(excludeAgentId = ''): Promise<TransferTarget[]> {
    const merged = new Map<string, TransferTarget>();
    const exclude = String(excludeAgentId || '').trim().toLowerCase();

    /**
     * Dedupe strategy:
     * - key by AgentId (case-insensitive)
     * - prefer SDK-sourced entries over non-SDK entries
     * - prefer entries that include status over entries without status
     */
    const addAgents = (agents: TransferTarget[]) => {
      for (const agent of agents) {
        const id = String(agent?.AgentId || '').trim();
        if (!id || id.toLowerCase() === exclude) {
          continue;
        }
        const key = id.toLowerCase();
        const existing = merged.get(key);
        if (!existing || (agent.fromSdk && !existing.fromSdk) || (agent.status && !existing.status)) {
          merged.set(key, { ...agent, AgentId: id });
        }
      }
    };

    if (!this.ozonetelSDKCore.isAgentInitialized()) {
      return [];
    }

    // Primary source: SDK transfer list for live, transfer-eligible agent targets.
    try {
      const transferResult = await this.ozonetelSDKCore.getTransferList(1);
      this.cachedTransferList = transferResult;
      addAgents(this.parseAgentList(transferResult, true));
      console.log('[OzonetelTransfer] SDK getTransferList agents:', transferResult);
    } catch (e) {
      console.warn('[OzonetelTransfer] SDK getTransferList failed', e);
    }

    // Secondary source: SDK conference list can expose additional currently available agents.
    try {
      const conferenceResult = await this.ozonetelSDKCore.getConferenceList(1);
      addAgents(this.parseAgentList(conferenceResult, true));
      console.log('[OzonetelTransfer] SDK getConferenceList agents:', conferenceResult);
    } catch (e) {
      console.warn('[OzonetelTransfer] SDK getConferenceList failed', e);
    }

    // Last fallback: only query server when SDK lists are empty.
    if (merged.size === 0) {
      try {
        const res: any = await firstValueFrom(this.callingService.getOzonetelOnlineAgents());
        addAgents(this.parseAgentList(res?.data, true).map((a) => ({ ...a, fromSdk: false })));
        console.log('[OzonetelTransfer] Ozonetel getAgentLogins agents:', res?.data);
      } catch (e) {
        console.warn('[OzonetelTransfer] Ozonetel getAgentLogins failed', e);
      }
    }

    // Default unresolved source flag to SDK=true and return alphabetically sorted display list.
    return Array.from(merged.values())
      .map((agent) => ({ ...agent, fromSdk: agent.fromSdk !== false }))
      .sort((a, b) =>
      a.AgentId.localeCompare(b.AgentId, undefined, { sensitivity: 'base' })
    );
  }

  /**
   * Resolves UI input to canonical target object.
   * Supports matching by:
   * - AgentId (display id)
   * - transferTo (SDK value)
   * - Extension (legacy/alternate id in some tenants)
   */
  resolveTransferTarget(selectedId: string, agents: TransferTarget[]): TransferTarget | null {
    const needle = String(selectedId || '').trim().toLowerCase();
    if (!needle) {
      return null;
    }
    return (
      agents.find(
        (a) =>
          a.AgentId.toLowerCase() === needle ||
          a.transferTo.toLowerCase() === needle ||
          (a.Extension && a.Extension.toLowerCase() === needle)
      ) || null
    );
  }

  async resolveTransferTargetOnline(
    selectedId: string,
    agents: TransferTarget[] = []
  ): Promise<TransferTarget | null> {
    const target = String(selectedId || '').trim();
    if (!target) {
      return null;
    }
    if (this.ozonetelSDKCore.isAgentInitialized()) {
      try {
        const transferList = await this.ozonetelSDKCore.getTransferList(1);
        this.cachedTransferList = transferList;
        const sdkAgents = this.parseAgentList(transferList, true);
        const fromSdk = this.resolveTransferTarget(target, sdkAgents);
        if (fromSdk) {
          return fromSdk;
        }
      } catch {
      }
    }
    return this.resolveTransferTarget(target, agents);
  }

  /**
   * Public warm transfer entry point used by CallingComponent.
   *
   * SDK mode:
   * - resolves selected agent to transferTo value
   * - executes SDK transfer call
   *
   * Legacy non-SDK fallback:
   * - uses server transfer endpoint through OzonetelController
   */
  async warmTransferToAgent(
    targetAgentId: string,
    inputObj: any,
    diallerController?: OzonetelController,
    agents: TransferTarget[] = []
  ): Promise<WarmTransferResult> {
    const target = String(targetAgentId || '').trim();
    if (!target) {
      return { success: false, message: 'Select an agent to transfer' };
    }
    const resolved = this.resolveTransferTarget(target, agents);
    const transferTo = resolved?.transferTo || target;
    if (this.ozonetelSDKCore.isAgentInitialized()) {
      return this.blindTransferViaSdk(transferTo, resolved, inputObj, diallerController);
    }
    if (diallerController) {
      return this.blindTransferViaRest(target, inputObj, diallerController, resolved);
    }
    return { success: false, message: 'Dialer not ready for transfer' };
  }

  /**
   * Best-effort helper to tag incoming call events as transfer legs.
   * Used only for client UI status/badges.
   */
  isTransferredCall(eventData: any): boolean {
    if (!eventData || typeof eventData !== 'object') {
      return false;
    }
    if (
      eventData.transferCall === true ||
      eventData.isTransfer === true ||
      eventData.transfer === true ||
      eventData.isTransferredCall === true
    ) {
      return true;
    }
    const type = String(
      eventData.callType ??
        eventData.calltype ??
        eventData.type ??
        eventData.mode ??
        eventData.transferType ??
        ''
    ).toLowerCase();
    if (type.includes('transfer')) {
      return true;
    }
    const subType = String(eventData.subCallType ?? eventData.callSubType ?? '').toLowerCase();
    if (subType.includes('transfer')) {
      return true;
    }
    const status = String(eventData.status ?? eventData.callStatus ?? eventData.callState ?? '').toLowerCase();
    return status.includes('transfer');
  }

  /**
   * SDK transfer with REST fallback. Manual calls prefer warm transfer (blind often rejected by Ozonetel).
   */
  private async blindTransferViaSdk(
    transferTo: string,
    resolved: TransferTarget | null,
    inputObj: any,
    diallerController?: OzonetelController
  ): Promise<WarmTransferResult> {
    try {
      await this.ozonetelSDKCore.forceUnholdActiveCall();
      const [transferList, callData] = await Promise.all([
        this.ozonetelSDKCore.getTransferList(1),
        this.ozonetelSDKCore.getCallData().catch(() => null),
      ]);
      this.cachedTransferList = transferList;
      const manual = this.isManualCall(callData);
      const transferCandidates = this.getTransferToCandidates(resolved, transferList, transferTo);
      // Ozonetel blind transfer to agent is not supported on manual legs — try warm SDK first, then REST.
      const attempts: Array<{ mode: string; warmOrBlind: number }> = manual
        ? [
            { mode: '1', warmOrBlind: 3 },
            { mode: 'Agent', warmOrBlind: 3 },
            { mode: '1', warmOrBlind: 1 },
            { mode: 'Agent', warmOrBlind: 1 },
          ]
        : [
            { mode: '1', warmOrBlind: 1 },
            { mode: 'Agent', warmOrBlind: 1 },
            { mode: '1', warmOrBlind: 3 },
            { mode: 'Agent', warmOrBlind: 3 },
          ];
      let lastMsg = '';
      for (const candidate of transferCandidates) {
        for (const { mode, warmOrBlind } of attempts) {
          console.log('[OzonetelTransfer] initTransferCall', {
            mode,
            warmOrBlind,
            transferTo: candidate,
            manual,
          });
          const result = await this.ozonetelSDKCore.initTransferCall(
            mode,
            warmOrBlind,
            transferList,
            candidate,
            ''
          );
          if (this.isSdkTransferSuccess(result)) {
            return this.blindTransferSuccess(warmOrBlind === 1 && !manual);
          }
          lastMsg = this.extractSdkMessage(result) || lastMsg;
        }
      }
      if (diallerController) {
        // Server CallControl CONFERENCE when SDK initTransferCall fails (common on manual calls).
        const restTarget =
          resolved?.Extension || resolved?.transferTo || resolved?.AgentId || transferTo;
        const rest = await this.blindTransferViaRest(
          restTarget,
          inputObj,
          diallerController,
          resolved,
          callData
        );
        if (rest.success) {
          return rest;
        }
        lastMsg = rest.message || lastMsg;
      }
      return { success: false, message: lastMsg || 'Transfer failed' };
    } catch (e: any) {
      return { success: false, message: e?.message || 'Transfer failed' };
    }
  }

  private blindTransferSuccess(trueBlind: boolean): WarmTransferResult {
    return {
      success: true,
      message: 'Call transferred',
      releaseAgentA: true,
      blindTransfer: trueBlind,
      // Warm/manual REST needs longer before Agent A hangs up so Agent B can ring.
      releaseDelayMs: trueBlind ? 2000 : 5000,
    };
  }

  private async blindTransferViaRest(
    targetAgentId: string,
    inputObj: any,
    diallerController: OzonetelController,
    resolved?: TransferTarget | null,
    callData?: any
  ): Promise<WarmTransferResult> {
    try {
      const liveCallData =
        callData ||
        (this.ozonetelSDKCore.isAgentInitialized()
          ? await this.ozonetelSDKCore.getCallData().catch(() => null)
          : null);
      const merged = { ...(liveCallData || {}), ...(inputObj || {}) };
      const ucid = merged.ucid || merged.UCID || merged.monitorUcid;
      const did = merged.did || merged.DID;
      const agentPhoneName =
        merged.agentPhoneName ||
        merged.phoneName ||
        merged.agentExtension ||
        merged.extension ||
        merged.agentId;
      const redirectId =
        resolved?.Extension ||
        resolved?.transferTo ||
        resolved?.AgentId ||
        targetAgentId;
      if (!ucid || !did || !agentPhoneName) {
        return { success: false, message: 'Missing call details for transfer (ucid/did)' };
      }
      const res: any = await firstValueFrom(
        diallerController.transferCall({
          RedirectToAgentId: redirectId,
          ucid,
          did,
          agentPhoneName,
          mobileNum: inputObj?.phoneNo,
          Name: inputObj?.fullName,
          clientId: inputObj?.clientId,
        })
      );
      if (res?.resultShort === 'Success' || res?.resultShort === 'success') {
        return this.blindTransferSuccess(false);
      }
      return { success: false, message: res?.resultLong || 'Transfer failed' };
    } catch (e: any) {
      return { success: false, message: e?.message || 'Transfer failed' };
    }
  }

  private isManualCall(callData: any): boolean {
    const type = String(
      callData?.callType ?? callData?.calltype ?? callData?.type ?? callData?.mode ?? ''
    ).toLowerCase();
    return (
      type.includes('manual') ||
      type.includes('outbound') ||
      type.includes('preview') ||
      type.includes('progressive') ||
      type === 'false'
    );
  }

  /**
   * Converts arbitrary agent-list payload into normalized targets.
   * Optionally filters out offline/logged-out agents.
   */
  private parseAgentList(result: any, filterOffline: boolean): TransferTarget[] {
    const items = this.extractListItems(result);
    return items
      .map((item) => this.toTransferTarget(item))
      .filter((item): item is TransferTarget => !!item)
      .filter((item) => !filterOffline || this.isOnlineAgent(item));
  }

  /** Returns true for states that are not explicitly offline/logged-out. */
  private isOnlineAgent(agent: TransferTarget): boolean {
    const status = String(agent.status || '').toLowerCase();
    if (!status) {
      return true;
    }
    if (
      status.includes('offline') ||
      status.includes('logout') ||
      status.includes('logged out') ||
      status.includes('not logged')
    ) {
      return false;
    }
    return true;
  }

  /**
   * Attempts to extract an array of records from heterogeneous SDK/API responses.
   * Handles:
   * - arrays directly
   * - common array keys
   * - object maps where values are agent records
   */
  private extractListItems(result: any): any[] {
    if (!result) {
      return [];
    }
    let body = result;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        return [];
      }
    }
    if (Array.isArray(body)) {
      return body;
    }
    const arrayKeys = [
      'data',
      'list',
      'agents',
      'agentList',
      'agentLogins',
      'AgentLogins',
      'loginAgents',
      'transferList',
      'conferenceList',
      'response',
      'result',
      'items',
      'values',
      'message',
    ];
    for (const key of arrayKeys) {
      const value = body?.[key];
      if (Array.isArray(value)) {
        return value;
      }
      if (value && typeof value === 'object') {
        return Object.values(value);
      }
    }
    if (body?.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
      const nested = Object.values(body.data);
      if (nested.length && nested.every((v) => v && typeof v === 'object')) {
        return nested;
      }
    }
    if (body && typeof body === 'object') {
      const values = Object.values(body);
      if (values.length && values.every((v) => v && typeof v === 'object')) {
        return values;
      }
    }
    return [];
  }

  /**
   * Normalizes one raw SDK/API record to TransferTarget.
   * Keeps both display id (AgentId) and API-safe transfer id (transferTo).
   */
  private toTransferTarget(item: any): TransferTarget | null {
    if (!item) {
      return null;
    }
    if (typeof item === 'string' || typeof item === 'number') {
      const id = String(item).trim();
      return id ? { AgentId: id, transferTo: id, label: id, fromSdk: true } : null;
    }
    const transferTo = String(
      item.agentName ||
        item.AgentName ||
        item.userName ||
        item.UserName ||
        item.user ||
        item.User ||
        item.transferTo ||
        item.agentId ||
        item.agentID ||
        item.AgentId ||
        item.id ||
        item.name ||
        item.extension ||
        item.Extension ||
        item.value ||
        ''
    ).trim();
    const id = String(
      item.AgentId ||
        item.agentId ||
        item.agentID ||
        item.userName ||
        item.UserName ||
        item.id ||
        item.name ||
        item.agentName ||
        item.AgentName ||
        transferTo ||
        ''
    ).trim();
    if (!id || !transferTo) {
      return null;
    }
    const status = this.extractAgentStatus(item);
    const extension = String(item.Extension || item.extension || item.extentionId || '').trim();
    const displayName = String(item.agentName || item.AgentName || item.name || item.label || '').trim();
    const label = status
      ? `${id} (${status})`
      : displayName && displayName.toLowerCase() !== id.toLowerCase()
        ? `${id} (${displayName})`
        : extension && extension !== id
          ? `${id} (${extension})`
          : id;
    return {
      AgentId: id,
      transferTo,
      Extension: extension || undefined,
      label,
      status: status || undefined,
      fromSdk: true,
    };
  }

  /** Extracts agent presence/status from common response fields. */
  private extractAgentStatus(item: any): string {
    return String(
      item.status ||
        item.Status ||
        item.agentStatus ||
        item.AgentStatus ||
        item.state ||
        item.State ||
        item.currentState ||
        item.loginStatus ||
        ''
    ).trim();
  }

  private getTransferToCandidates(
    resolved: TransferTarget | null,
    transferList: any,
    fallback: string
  ): string[] {
    const out: string[] = [];
    const add = (value: unknown) => {
      const id = String(value ?? '').trim();
      if (id && !out.includes(id)) {
        out.push(id);
      }
      if (id.includes('~')) {
        for (const part of id.split('~')) {
          const piece = String(part || '').trim();
          if (piece && !out.includes(piece)) {
            out.push(piece);
          }
        }
      }
    };
    add(resolved?.transferTo);
    add(resolved?.AgentId);
    add(resolved?.Extension);
    add(fallback);
    const needles = new Set(
      [resolved?.AgentId, resolved?.transferTo, resolved?.Extension, fallback]
        .map((v) => String(v || '').trim().toLowerCase())
        .filter(Boolean)
    );
    for (const item of this.extractListItems(transferList)) {
      if (typeof item === 'string' || typeof item === 'number') {
        const raw = String(item).trim();
        if (raw && needles.has(raw.toLowerCase())) {
          add(raw);
        }
        continue;
      }
      const fields = [
        item?.agentName,
        item?.AgentName,
        item?.userName,
        item?.UserName,
        item?.user,
        item?.User,
        item?.transferTo,
        item?.value,
        item?.agentId,
        item?.agentID,
        item?.AgentId,
        item?.extension,
        item?.Extension,
      ].map((v) => String(v || '').trim());
      if (!fields.some((v) => v && needles.has(v.toLowerCase()))) {
        continue;
      }
      for (const field of fields) {
        add(field);
      }
      const agentId = String(item?.agentId || item?.AgentId || item?.agentID || '').trim();
      const agentName = String(item?.agentName || item?.AgentName || item?.userName || '').trim();
      if (agentId && agentName) {
        add(`${agentId}~${agentName}`);
        add(`${agentName}~${agentId}`);
      }
    }
    return out.length ? out : [String(fallback || '').trim()].filter(Boolean);
  }

  private isSdkTransferSuccess(result: any): boolean {
    if (!result) {
      return false;
    }
    // Ambiguous SDK ack ({ type: 'response', utid }) is not success — avoids premature Agent A hangup.
    const status = String(result?.status ?? result?.Status ?? '').toLowerCase();
    const resp = String(result?.resp ?? result?.Resp ?? '');
    const msg = this.extractSdkMessage(result).toLowerCase();
    if (status === 'error' || status === 'failure' || status === 'failed') {
      return false;
    }
    if (msg.includes('failed') || msg.includes('invalid')) {
      return false;
    }
    if (status === 'success' || result?.success === true) {
      return true;
    }
    if (resp === 'Success' || resp.includes('Success')) {
      return true;
    }
    return false;
  }

  private extractSdkMessage(result: any): string {
    const raw =
      result?.message ??
      result?.Message ??
      result?.resp ??
      result?.Resp ??
      result?.resultLong ??
      result?.error ??
      '';
    if (Array.isArray(raw)) {
      return raw.map((v) => String(v)).filter(Boolean).join(', ');
    }
    return String(raw || '');
  }
}
